import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, app } from '../../lib/firebase';
import {
  ServerStackIcon,
  CircleStackIcon,
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

const SystemDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState<{ name: string; count: number }[]>([]);
  const [firebaseConnected, setFirebaseConnected] = useState(false);

  useEffect(() => {
    fetchSystemInfo();
  }, []);

  const fetchSystemInfo = async () => {
    try {
      setLoading(true);

      const collectionNames = [
        'organizations', 'users', 'subscriptions', 'packages',
        'customers', 'products', 'quotations', 'projects',
        'tasks', 'invoices', 'notifications', 'emailTemplates',
        'customFields', 'activityLogs'
      ];

      const results = await Promise.all(
        collectionNames.map(async name => {
          try {
            const snapshot = await getDocs(collection(db, name));
            return { name, count: snapshot.size };
          } catch {
            return { name, count: 0 };
          }
        })
      );

      setCollections(results.sort((a, b) => b.count - a.count));
      setFirebaseConnected(true);
    } catch (error) {
      console.error('Error fetching system info:', error);
      setFirebaseConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const firebaseProjectId = app.options.projectId || 'N/A';
  const isDev = process.env.NODE_ENV === 'development';

  const envInfo = [
    { label: 'Firebase csatlakozás', value: firebaseConnected ? 'Aktív' : 'Hiba', status: firebaseConnected },
    { label: 'Környezet', value: isDev ? 'Development' : 'Production', status: !isDev },
    { label: 'Firebase Project', value: firebaseProjectId, status: null },
    { label: 'Firebase Auth Domain', value: (app.options.authDomain as string) || 'N/A', status: null },
    { label: 'Storage Bucket', value: (app.options.storageBucket as string) || 'N/A', status: null },
    { label: 'Betöltve', value: new Date().toLocaleString('hu-HU'), status: null },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Rendszer</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Rendszer állapot, adatbázis és környezet</p>
        </div>
        <button
          onClick={fetchSystemInfo}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1.5" />
          Frissítés
        </button>
      </div>

      {/* System Status */}
      <div className="crm-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-slate-100 dark:bg-slate-900/30 rounded-lg">
            <ServerStackIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Rendszer állapot</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Szolgáltatások és kapcsolatok</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {envInfo.map((info) => (
            <div key={info.label} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-2">
                {info.status === true && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
                {info.status === false && <XCircleIcon className="h-4 w-4 text-red-500" />}
                {info.status === null && <InformationCircleIcon className="h-4 w-4 text-gray-400" />}
                <span className="text-sm text-gray-600 dark:text-gray-400">{info.label}</span>
              </div>
              <span className={`text-sm font-medium ${info.status === true ? 'text-green-600 dark:text-green-400' : info.status === false ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                {info.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Firestore Collections */}
      <div className="crm-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <CircleStackIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Firestore kollekciók</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Dokumentumok száma kollekciónként</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {collections.map(col => (
            <div key={col.name} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{col.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {col.count} {col.count === 1 ? 'dokumentum' : 'dokumentum'}
              </div>
              {col.count > 0 && (
                <div className="mt-1.5 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1">
                  <div
                    className="bg-blue-500 h-1 rounded-full"
                    style={{ width: `${Math.min((col.count / Math.max(...collections.map(c => c.count))) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* System Info */}
      <div className="crm-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <CpuChipIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Technológiai stack</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Rendszer komponensek és verziók</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { name: 'React', version: '18.x' },
            { name: 'TypeScript', version: '5.x' },
            { name: 'Tailwind CSS', version: '3.x' },
            { name: 'Firebase', version: '10.x' },
            { name: 'Vite', version: '5.x' },
            { name: 'React Router', version: '6.x' },
          ].map(tech => (
            <div key={tech.name} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{tech.name}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">{tech.version}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SystemDashboard;
